import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Truck, AlertTriangle, Image as ImageIcon, ChevronDown, ChevronRight, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

const VEHICLE_TYPES = [
  // Cars / small
  { value: "car",             label: "Car" },
  { value: "motorcycle",      label: "Motorcycle / Moped" },
  { value: "pickup",          label: "Pickup truck" },
  // Vans by size (UK convention)
  { value: "small_van",       label: "Small van (car-derived, e.g. Berlingo)" },
  { value: "swb_van",         label: "SWB van — Short Wheelbase" },
  { value: "mwb_van",         label: "MWB van — Medium Wheelbase" },
  { value: "lwb_van",         label: "LWB van — Long Wheelbase" },
  { value: "xlwb_van",        label: "XLWB van — Extra Long Wheelbase" },
  { value: "luton_van",       label: "Luton box van (tail-lift)" },
  // HGVs
  { value: "hgv_75t",         label: "7.5 Tonne truck (Class C1)" },
  { value: "hgv_18t",         label: "18 Tonne rigid truck (Class C)" },
  { value: "hgv_26t",         label: "26 Tonne rigid truck (Class C)" },
  { value: "hgv_artic",       label: "Articulated lorry (Class C+E)" },
  // Other
  { value: "trailer",         label: "Trailer" },
  { value: "other",           label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(VEHICLE_TYPES.map(t => [t.value, t.label]));

const EMPTY = {
  registration: "", make: "", model: "", year: "", vehicle_type: "",
  road_tax_renew_date: "", insurance_renew_date: "", mot_renew_date: "",
  notes: "", is_active: true,
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function expiryStatus(iso) {
  if (!iso) return { tone: "muted", label: "—", days: null };
  const t = new Date(todayISO()); const d = new Date(iso);
  const diff = Math.round((d - t) / 86400000);
  if (diff < 0) return { tone: "red", label: `Overdue ${Math.abs(diff)}d`, days: diff };
  if (diff <= 30) return { tone: "amber", label: `${diff}d left`, days: diff };
  return { tone: "green", label: iso, days: diff };
}

function ExpiryCell({ iso }) {
  const s = expiryStatus(iso);
  if (s.tone === "muted") return <span className="text-slate-400">—</span>;
  const tones = {
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    green: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <div className="space-y-0.5">
      <div className="text-sm text-slate-900 tabular-nums">{iso || "—"}</div>
      <span className={`inline-block text-[10px] uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${tones[s.tone]}`}>
        {s.label}
      </span>
    </div>
  );
}

export default function Vehicles() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vehicles").then(r => setRows(r.data || [])).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    const isActiveQ = ["active", "inactive"].includes(q);
    return rows.filter(v => {
      if (isActiveQ) {
        return q === "active" ? v.is_active !== false : v.is_active === false;
      }
      return [v.registration, v.make, v.model, String(v.year || ""), v.notes, TYPE_LABEL[v.vehicle_type]]
        .filter(Boolean).some(s => s.toLowerCase().includes(q));
    });
  }, [rows, search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      registration: v.registration || "",
      make: v.make || "",
      model: v.model || "",
      year: v.year || "",
      vehicle_type: v.vehicle_type || "",
      road_tax_renew_date: v.road_tax_renew_date || "",
      insurance_renew_date: v.insurance_renew_date || "",
      mot_renew_date: v.mot_renew_date || "",
      notes: v.notes || "",
      is_active: v.is_active !== false,
    });
  };

  const save = async () => {
    const payload = {
      ...form,
      registration: (form.registration || "").trim().toUpperCase(),
      year: form.year === "" || form.year === null ? null : Number(form.year),
    };
    if (!payload.registration) { toast.error("Registration is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else         await api.post("/vehicles", payload);
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setEditing(null); setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vehicle ${v.registration}?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const startNew = () => { openNew(); setOpen(true); };
  const startEdit = (v) => { openEdit(v); setOpen(true); };
  const onClose = () => { setOpen(false); setEditing(null); setForm(EMPTY); };
  const toggleExpand = (id) => setExpandedId(curr => curr === id ? null : id);

  return (
    <div data-testid="vehicles-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Fleet</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">D4PS Vehicles</h1>
          <p className="text-sm text-slate-600 mt-1">Your company-owned fleet — registrations, road tax, insurance and MOT renewals at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="vehicles-export-pdf" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/vehicles/export.pdf`)} download rel="noopener">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </a>
          </Button>
          <Button asChild variant="outline" data-testid="vehicles-export-xlsx" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/vehicles/export.xlsx`)} download rel="noopener">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
            </a>
          </Button>
          <Button data-testid="vehicles-add-btn" onClick={startNew} className="rounded-sm bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-1.5" /> Add vehicle
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="vehicles-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search reg, make, model or "active"/"inactive"'
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
        <div className="text-xs text-slate-500 tabular-nums">{filtered.length} of {rows.length}</div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vehicles-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th className="w-8"></Th>
                <Th>Registration</Th>
                <Th>Make / Model</Th>
                <Th>Type</Th>
                <Th>Year</Th>
                <Th>Road tax</Th>
                <Th>Insurance</Th>
                <Th>MOT</Th>
                <Th>Status</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-500 text-sm">
                    <Truck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No vehicles yet. Add your first one.
                  </td>
                </tr>
              )}
              {filtered.map((v) => {
                const expiries = [v.road_tax_renew_date, v.insurance_renew_date, v.mot_renew_date].map(expiryStatus);
                const worstTone = expiries.some(e => e.tone === "red") ? "red"
                  : expiries.some(e => e.tone === "amber") ? "amber" : "ok";
                const isOpen = expandedId === v.id;
                return (
                  <Fragment key={v.id}>
                  <tr className="border-t border-slate-200 hover:bg-slate-50">
                    <Td className="pr-0">
                      <button
                        data-testid={`vehicle-expand-${v.id}`}
                        onClick={() => toggleExpand(v.id)}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-600"
                        title={isOpen ? "Hide photos & documents" : "Show photos & documents"}
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </Td>
                    <Td>
                      <div className="font-medium text-slate-900 tracking-wider">{v.registration}</div>
                    </Td>
                    <Td><div className="text-slate-900">{[v.make, v.model].filter(Boolean).join(" ") || "—"}</div></Td>
                    <Td><div className="text-slate-700 text-xs">{TYPE_LABEL[v.vehicle_type] || "—"}</div></Td>
                    <Td><div className="tabular-nums">{v.year || "—"}</div></Td>
                    <Td><ExpiryCell iso={v.road_tax_renew_date} /></Td>
                    <Td><ExpiryCell iso={v.insurance_renew_date} /></Td>
                    <Td><ExpiryCell iso={v.mot_renew_date} /></Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        {v.is_active === false
                          ? <Badge variant="outline" className="rounded-sm">Inactive</Badge>
                          : <Badge className="rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Active</Badge>}
                        {worstTone === "red" && <AlertTriangle className="w-3.5 h-3.5 text-red-600" title="Has overdue renewals" />}
                        {worstTone === "amber" && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Renewals due soon" />}
                      </div>
                    </Td>
                    <Td className="text-right pr-4">
                      <Button size="sm" variant="ghost" data-testid={`vehicle-photos-${v.id}`} onClick={() => toggleExpand(v.id)} className="rounded-sm h-8 w-8 p-0" title="Photos & documents">
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`vehicle-edit-${v.id}`} onClick={() => startEdit(v)} className="rounded-sm h-8 w-8 p-0">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`vehicle-delete-${v.id}`} onClick={() => remove(v)} className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/70 border-t border-slate-200">
                      <td colSpan={10} className="px-6 py-5">
                        <FileUploader
                          entityType="vehicle"
                          entityId={v.id}
                          label={`Photos & documents — ${v.registration}`}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="vehicle-dialog" className="rounded-sm sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Registration *">
              <Input data-testid="veh-reg" value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} placeholder="AB12 CDE" className="rounded-sm border-slate-300 uppercase" />
            </Field>
            <Field label="Year">
              <Input data-testid="veh-year" type="number" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2024" className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Make">
              <Input data-testid="veh-make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Mercedes" className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Model">
              <Input data-testid="veh-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Sprinter" className="rounded-sm border-slate-300" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Vehicle type">
                <Select value={form.vehicle_type || "__none"} onValueChange={(v) => setForm({ ...form, vehicle_type: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="veh-type" className="rounded-sm border-slate-300"><SelectValue placeholder="Pick a type" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none">— Not set —</SelectItem>
                    {VEHICLE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Road tax renew date">
              <Input data-testid="veh-tax" type="date" value={form.road_tax_renew_date} onChange={(e) => setForm({ ...form, road_tax_renew_date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Insurance renew date">
              <Input data-testid="veh-ins" type="date" value={form.insurance_renew_date} onChange={(e) => setForm({ ...form, insurance_renew_date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="MOT renew date">
              <Input data-testid="veh-mot" type="date" value={form.mot_renew_date} onChange={(e) => setForm({ ...form, mot_renew_date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Active">
              <div className="h-10 flex items-center gap-3">
                <Switch data-testid="veh-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm text-slate-600">{form.is_active ? "Active" : "Inactive (off-road)"}</span>
              </div>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <Input data-testid="veh-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any notes about this vehicle…" className="rounded-sm border-slate-300" />
              </Field>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button data-testid="veh-save" onClick={async () => { await save(); setOpen(false); }} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add vehicle")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

